/**
 * WebView Bridge Utility
 * Provides communication between React application and VB.NET desktop application via WebView2
 */

// Host sets this via ExecuteScriptAsync so the app can detect desktop even before message is received
declare global {
  interface Window {
    __ASPEN_EMBEDDED_DESKTOP__?: boolean;
    chrome?: {
      webview?: {
        postMessage: (message: any) => void;
        addEventListener: (
          type: string,
          listener: (event: any) => void,
        ) => void;
        removeEventListener: (
          type: string,
          listener: (event: any) => void,
        ) => void;
      };
    };
  }
}

// Message types for communication with desktop app
export const WebViewMessageTypes = {
  // Messages from React to Desktop
  OPEN_WORK_ORDER: "OPEN_WORK_ORDER",
  // Screen names
  SCREEN_WORK_ORDER_DETAILS: "WorkOrderDetails",

  // Messages from Desktop to React
  NAVIGATE_TO: "NAVIGATE_TO",
} as const;

export interface WebViewMessage {
  type: string;
  screen?: string;
  customerId?: number;
  customerName?: string;
  invoiceDocId?: number;
  documentNumber?: string;
  data?: any;
}

/**
 * Check if the app is running inside a WebView2 control
 */
export function isRunningInWebView2(): boolean {
  return !!window.chrome?.webview;
}

/**
 * True when the app should offer both Service Queue dialog and desktop POS open actions.
 */
export function shouldOfferDualWorkOrderOpen(): boolean {
  return isRunningInWebView2();
}

/**
 * Check if the app is embedded in the Aspen desktop (host sets window.__ASPEN_EMBEDDED_DESKTOP__ or sends "host:desktop").
 */
export function isEmbeddedInDesktop(): boolean {
  return !!(
    typeof window !== "undefined" &&
    (window as Window).__ASPEN_EMBEDDED_DESKTOP__
  );
}

/**
 * Post a message to the desktop application via WebView2
 * @param message The message object to send
 */
export function postMessageToDesktop(message: WebViewMessage): void {
  if (isRunningInWebView2()) {
    try {
      console.log("[WebViewBridge] Sending message to desktop:", JSON.stringify(message));
      window.chrome!.webview!.postMessage(message);
      console.log("[WebViewBridge] postMessage completed successfully");
    } catch (error) {
      console.error("[WebViewBridge] Error sending message to desktop:", error);
    }
  } else {
    console.warn("[WebViewBridge] Not running in WebView2, message not sent:", message);
  }
}

/**
 * Open a Work Order in the desktop application
 * @param invoiceDocId The invoice document ID
 * @param documentNumber The document number (WO#)
 * @param customerId Optional customer ID
 * @param customerName Optional customer name
 */
export function openWorkOrderInDesktop(invoiceDocId: number): void {
  console.log("[WebViewBridge] openWorkOrderInDesktop called with invoiceDocId:", invoiceDocId, "type:", typeof invoiceDocId);
  postMessageToDesktop({
    type: WebViewMessageTypes.OPEN_WORK_ORDER,
    invoiceDocId,
  });
}

/**
 * Subscribe to messages posted from the Aspen desktop host (e.g. "host:desktop").
 */
export function subscribeToDesktopHostMessages(
  listener: (data: unknown) => void,
): () => void {
  if (!isRunningInWebView2()) {
    return () => {};
  }

  const handler = (event: { data: unknown }) => listener(event.data);
  window.chrome!.webview!.addEventListener("message", handler);
  return () => window.chrome?.webview?.removeEventListener("message", handler);
}

/**
 * Listen for messages from the desktop application
 * @param callback Function to call when a message is received
 * @returns Cleanup function to remove the listener
 */
export function listenForDesktopMessages(
  callback: (message: WebViewMessage) => void,
): () => void {
  if (!isRunningInWebView2()) {
    // console.log(
    //   "[WebViewBridge] Not running in WebView2, message listener not added"
    // );
    return () => {};
  }

  const handler = (event: any) => {
    try {
      const message =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      //console.log("[WebViewBridge] Message received from desktop:", message);
      callback(message as WebViewMessage);
    } catch (error) {
      // console.error(
      //   "[WebViewBridge] Error processing message from desktop:",
      //   error
      // );
    }
  };

  window.chrome!.webview!.addEventListener("message", handler);

  return () => {
    window.chrome!.webview!.removeEventListener("message", handler);
  };
}
